import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import WSDataGrid, { IColumns } from "../../../Components/DataGrid/WSDataGrid";
import { useNavigate } from "react-router-dom";
import { callAPI, Method, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";
import { resetDataComponent } from "../../../StateManagement/actions";
import { useDispatch } from "react-redux";
import config from '../../../Config/clientConfig.json'
import { useForm } from "react-hook-form";
import { UpdateStatus } from "../../Reports/subComponents/UpdateStatus";

export const QuotationList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newStatus: "",
    },
  });

  useEffect(() => {
    updateMainHeader("Sales");
  }, []);

  breadCrumbsStore.updateBreadCrumbs([
    { "Quotation Management": "/invoice/list" },
  ]);


  const statusOptions = [
    { value: "Draft", label: "Draft" },
    { value: "Sent", label: "Sent" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Converted", label: "Converted" },
  ];

  const handleUpdateStatus = async (data: any) => {
    try {
      if (!selectedId) return;

      const body = {
        Status: data.newStatus,
      };

      const response = await callAPI(
        `quotation/status/${selectedId}`,
        Method.PUT,
        body
      );

      if (response?.success) {
        showMessage("quotation status updated successfully!", "success");
        setStatusPopupOpen(false);
        reset();

        // 🔄 Refresh grid
        setRefreshKey((prev) => prev + 1);
      } else {
        showMessage(response?.message || "Failed to update status", "error");
      }
    } catch (error: any) {
      console.error("❌ Update Status Error:", error);
      showMessage(error?.message || "Something went wrong", "error");
    }
  };

  const source = {
    url: "quotation/list",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: [],
    type: "GET" as const,
  };

  const columns: IColumns[] = [
    {
      header: "Date",
      fieldName: "CreatedDate",
      type: "date"
    },
    {
      header: "Quotation No",
      fieldName: "QuotationNumber",
    },
    {
      header: "Customer",
      fieldName: "CustomerName",
    },
    {
      header: "Total Amount",
      fieldName: "TotalAmount",
      type: "amount"
    },
    {
      header: "Status",
      fieldName: "Status",
      type: "status"
    },

    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete", "Update Status", "Print"],
      actionCallback: (action: string, row: any) => {
        const quotationId = row.QuotationId;

        if (!quotationId) {
          console.error("No valid ID found in row:", row);
          showMessage("Invalid quotation data - missing ID", "error");
          return;
        }

        if (action === "Edit") {
          navigate(`/quotation/${quotationId}`);
        }
        else if (action === "Print") {
          window.open(`/quotation/print/${quotationId}`, "_blank", "noopener,noreferrer");
        }
        else if (action === "Delete") {
          setSelectedId(quotationId);
          setPopupOpen(true);
        }
        else if (action === "Update Status") {
          setSelectedId(quotationId);
          setStatusPopupOpen(true);
        }
      }
    },
  ];

  const filterFields = [
    { type: "text", fieldName: "transaction_type", label: "Transaction Type", operator: "eq" },
    // { type: "text", fieldName: "quotation_no", label: "Quotation No" },
    // { type: "text", fieldName: "customer_name", label: "Customer Name" },

    { type: "date", fieldName: "start_date", label: "Start Date" },
    { type: "date", fieldName: "end_date", label: "End Date" },

    {
      type: "dropdown", fieldName: "status", label: "Status", options: config.quotationStatus.staticOptionData
    },

    // { type: "dropdown", fieldName: "warehouse_name", label: "Warehouse", options: warehouse },
    // { type: "dropdown", fieldName: "item_name", label: "Item", options: items },
  ];


  const handleClick = () => {
    navigate("/quotation");
  };

  return (
    <Grid p={3}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <WSDataGrid
            id="warehouseGrid"
            key={refreshKey}
            title="Quotation Management"
            headerType="modern"
            columns={columns}
            source={source}
            exportCsv
            rowsPerPageCount={10}
            filterFields={filterFields}
            searchBox={{ fieldName: "quotation_no" }}
            addButton={{ label: "Add New quotation", color: "secondary" }}
            onAddButtonClick={handleClick}

            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>

        <ConfirmationPopup
          type="DELETE"
          apiUrl="quotation/delete"
          id={selectedId}
          confirmMessage="Are you sure you want to delete this expense?"
          open={isPopupOpen}
          onClose={() => setPopupOpen(false)}
          onConfirm={(data: any) => {
            dispatch(resetDataComponent("expenseGrid"));
            if (data.success === true) {
              showMessage(data?.message, "success");
            } else {
              showMessage(data?.detail || "Delete failed", "error");
            }
          }}
        />

        <Dialog open={isStatusPopupOpen} onClose={() => setStatusPopupOpen(false)}>
          <DialogTitle>Update Quotation Status</DialogTitle>

          <DialogContent>
            <UpdateStatus
              control={control}
              errors={{}}
              register={register}
              id={selectedId}
              reset={reset}
              config={statusOptions}
            />
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setStatusPopupOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleUpdateStatus)}
              disabled={!isValid}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

      </Grid>
    </Grid>
  );
};
